/*
    Copyright 2020 Lauren Gyldenlove.
    This file is part of Duelmaster.

    Duelmaster is free software: you can redistribute it and/or modify
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Duelmaster is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with Foobar.  If not, see <https://www.gnu.org/licenses/>.
 */

import  React from 'react';
import './App.css';
import { PlayerView } from './components/Player';
import { BattleCalculator } from './components/BattleCalculator';
import { AppSettings } from './model/AppSettings';
import * as SettingsUtil from './model/AppSettings';
import MenuIcon from '@material-ui/icons/Menu';
import { MuiThemeProvider, makeStyles, Theme, createStyles, createMuiTheme } from '@material-ui/core/styles';
import * as Ledger from './model/Ledger';
import { EffectCalculator } from './components/EffectCalculator';
import * as Player from './model/Player';
import { SettingsToolItem } from './components/SettingsToolItem';
import { ResetDuelToolItem } from './components/ResetDuelToolItem';
import { CoinFlipToolItem } from './components/CoinFlipToolItem';
import { VictoryScreen } from './components/VictoryScreen';
import { AppBar, Toolbar, IconButton, Typography, Drawer, CssBaseline, List, Divider, Grid } from '@material-ui/core';
import bgImage from './assets/bg.png';
import { AboutScreenToolItem } from './components/AboutScreenToolItem';
import { DamageHistoryToolItem } from './components/DamageHistoryToolItem';
import * as ShortcutManager from './model/ShortcutManager';

export const AppContext = React.createContext<AppSettings>(SettingsUtil.loadFromLocalStorage());

const defaultDarkTheme = createMuiTheme({
	palette: {
		type: 'dark',
		primary: {
			light: '#ff0',
			main: '#cb5',
			dark: '#9a3',
			contrastText: '#000'
		}
	}
});

const drawerWidth = 240;
const useStyles = makeStyles((theme: Theme) => createStyles({
	root: {
		display: 'flex',
		background: `#000 url(${bgImage}) repeat center center`,
		minHeight: '100vh'
	},
	appBar: {
		transition: theme.transitions.create(['margin', 'width'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
	},
	appBarShift: {
		width: `calc(100% - ${drawerWidth}px)`,
		marginLeft: drawerWidth,
		transition: theme.transitions.create(['margin', 'width'], {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
	},
	menuButton: {
		marginRight: theme.spacing(2),
	},
	hide: {
		display: 'none',
	},
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
	},
	drawerHeader: {
		display: 'flex',
		alignItems: 'center',
		padding: theme.spacing(0, 1),
		// necessary for content to be below app bar
		...theme.mixins.toolbar,
		justifyContent: 'flex-end',
	},
	content: {
		flexGrow: 1,
		padding: theme.spacing(3),
		transition: theme.transitions.create('margin', {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		marginLeft: -drawerWidth,
	},
	contentShift: {
		transition: theme.transitions.create('margin', {
			easing: theme.transitions.easing.easeOut,
			duration: theme.transitions.duration.enteringScreen,
		}),
		marginLeft: 0,
		width: "100%",
		padding: "0.5em"
	},
}));
  

function App() {
	const [drawerOpen, setDrawerOpen] = React.useState(false);

	const appContext = React.useContext<AppSettings>(AppContext);
	const [initialLifePoints, setInitialLifePoints] = React.useState(appContext.initialLifePoints);
	const [matches, setMatches] = React.useState(appContext.matches);
	const [volume, setVolume] = React.useState(appContext.volume);
	const [leftPlayer, setLeftPlayer] = React.useState(appContext.leftPlayer);
	const [rightPlayer, setRightPlayer] = React.useState(appContext.rightPlayer);

	const [leftPlayerLedger, setLeftPlayerLedger] = React.useState(Ledger.create());
	const [rightPlayerLedger, setRightPlayerLedger] = React.useState(Ledger.create());

	const [winStateReached, setWinStateReached] = React.useState(false);
	const [winningPlayer, setWinningPlayer] = React.useState("");

	const settings: AppSettings = {
		"initialLifePoints": initialLifePoints, 
		"setInitialLifePoints": setInitialLifePoints,
		"matches": matches,
		"setMatches": setMatches,
		"leftPlayer": leftPlayer, 
		"setLeftPlayer": setLeftPlayer,
		"rightPlayer": rightPlayer,
		"setRightPlayer": setRightPlayer,
		"volume": volume,
		"setVolume": setVolume
	}
	const classes = useStyles();

	const isMatchWon = (): boolean => {
		return matches === rightPlayer.wins || matches === leftPlayer.wins
	}

	const resetRound = () => {
		setLeftPlayerLedger(Ledger.create());
		setRightPlayerLedger(Ledger.create());
	}

	const resetMatch = () => {
		resetRound();
		setRightPlayer(Player.setWin(rightPlayer, 0));
		setLeftPlayer(Player.setWin(leftPlayer, 0));
	}

	const resetDuel = (resetEntireMatch: boolean) => {
		if(resetEntireMatch) {
			resetMatch();
		} else {
			resetRound();
		}
	}

	const handleDrawerOpen = () => {
		setDrawerOpen(true);
	}

	const handleDrawerClose = () => {
		setDrawerOpen(false);
	}

	ShortcutManager.initialize();

	React.useEffect(() => {
		if (!winStateReached && settings.initialLifePoints <= Ledger.getTotalDamage(leftPlayerLedger)) {
			setRightPlayer(Player.addWin(rightPlayer));
			setWinStateReached(true);
			setWinningPlayer(rightPlayer.name);
		}

		if (!winStateReached && settings.initialLifePoints <= Ledger.getTotalDamage(rightPlayerLedger)) {
			setLeftPlayer(Player.addWin(leftPlayer));
			setWinStateReached(true);
			setWinningPlayer(leftPlayer.name);
		}
	}, [ settings.initialLifePoints, leftPlayerLedger, rightPlayerLedger, leftPlayer, rightPlayer, winStateReached ]);

	return (
		<MuiThemeProvider theme={defaultDarkTheme}>
			<div className={classes.root}>
				<CssBaseline />
				<AppContext.Provider value={settings}>
					<AppBar position="fixed">
						<Toolbar>
							<IconButton color="inherit" aria-label="open drawer" onClick={handleDrawerOpen} edge="start"><MenuIcon /></IconButton>
							<Typography variant="h6" noWrap>Duelmaster BETA</Typography>
						</Toolbar>
					</AppBar>
					<Drawer anchor="left" open={drawerOpen} onClose={handleDrawerClose}>
						<List>
							<DamageHistoryToolItem 
								leftPlayerLedger={leftPlayerLedger} 
								setLeftPlayerLedger={setLeftPlayerLedger}
								rightPlayerLedger={rightPlayerLedger} 
								setRightPlayerLedger={setRightPlayerLedger}
							/>
							<ResetDuelToolItem resetDuel={resetDuel} />
							<CoinFlipToolItem />
							<Divider />
							<SettingsToolItem />
							<AboutScreenToolItem />
						</List>
					</Drawer>
					<VictoryScreen 
						winStateReached={winStateReached} 
						winningPlayer={winningPlayer} 
						setWinStateReached={setWinStateReached} 
						matchWon={isMatchWon()}
						resetDuel={resetDuel}
					 />

					<div className={classes.contentShift}>
						<div className={classes.drawerHeader} />
						<Grid container justify="center" alignItems="center">
							<Grid item xs={5} md={4}>
								<PlayerView 
									player={leftPlayer} 
									initialLifePoints={initialLifePoints} 
									playerLedger={leftPlayerLedger}
									maxGames={matches}
								/>
							</Grid>
							<Grid item xs={2} md={1} className="t-center">
								<BattleCalculator 
										leftLedger={leftPlayerLedger} 
										setLeftLedger={setLeftPlayerLedger} 
										rightLedger={rightPlayerLedger} 
										setRightLedger={setRightPlayerLedger}
								/>
							</Grid>
							<Grid item xs={5} md={4}>
								<PlayerView 
									player={rightPlayer} 
									initialLifePoints={initialLifePoints} 
									playerLedger={rightPlayerLedger}
									maxGames={matches}
								/>		
							</Grid>
						</Grid>

						<Grid container justify="center" alignItems="center">
							<Grid item xs={12} md={9}>
								<EffectCalculator 
									leftLedger={leftPlayerLedger}
									rightLedger={rightPlayerLedger}
									setLeftLedger={setLeftPlayerLedger}
									setRightLedger={setRightPlayerLedger}
								/>
							</Grid>
						</Grid>
					</div>
				</AppContext.Provider>		
			</div>
		</MuiThemeProvider>
	);
}

export default App;
